// heatmapExportSvg — renders a standalone SVG string for a single appetite heatmap.
// Two modes: grid-only or grid+table. The SVG uses resolved colour values (not CSS
// variables) so it renders correctly outside the app, e.g. when imported into PowerPoint.

import type { GridConfig, AppetiteMap } from './types'
import type { HeatmapRisk } from '@/lib/api/reporting'
import {
  VIEWBOX_W,
  VIEWBOX_H,
  MARGIN,
  cellWidth,
  cellHeight,
  cellOrigin,
  appetiteKey,
} from './risk-grid-utils'

// --- Resolved theme colours (from CSS variables at export time) ---

/** Convert a CSS HSL variable value (space-separated, e.g. "120 40% 85%") to a hex colour.
 * PowerPoint's SVG renderer does not support hsl() in any format, so we must use hex. */
function hslToHex(h: number, s: number, l: number): string {
  s /= 100
  l /= 100
  const a = s * Math.min(l, 1 - l)
  const f = (n: number) => {
    const k = (n + h / 30) % 12
    const colour = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)
    return Math.round(255 * colour).toString(16).padStart(2, '0')
  }
  return `#${f(0)}${f(8)}${f(4)}`
}

function resolveHSL(varName: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback
  const raw = getComputedStyle(document.documentElement).getPropertyValue(varName).trim()
  if (!raw) return fallback
  // Parse space-separated HSL: "120 40% 85%"
  const parts = raw.split(/\s+/)
  if (parts.length >= 3) {
    const h = parseFloat(parts[0]!)
    const s = parseFloat(parts[1]!)
    const l = parseFloat(parts[2]!)
    if (!isNaN(h) && !isNaN(s) && !isNaN(l)) {
      return hslToHex(h, s, l)
    }
  }
  return fallback
}

function getThemeColours() {
  return {
    border: resolveHSL('--border', '#c4cad4'),
    mutedFg: resolveHSL('--muted-foreground', '#6b7280'),
    foreground: resolveHSL('--foreground', '#0a1628'),
    background: resolveHSL('--background', '#f3f4f7'),
    appetiteIn: resolveHSL('--grid-appetite-in', '#b8e6c0'),
    appetiteOut: resolveHSL('--grid-appetite-out', '#edb3b3'),
    dotResidual: resolveHSL('--grid-node-residual', '#2f6fbf'),
    dotInherent: resolveHSL('--grid-node-inherent', '#d98c1f'),
    destructive: resolveHSL('--destructive', '#e53e3e'),
  }
}

// --- Spiral offset maths (duplicated from RiskHeatmapDots for standalone rendering) ---

function spiralOffsets(capacity: number): { dx: number; dy: number }[] {
  const offsets: { dx: number; dy: number }[] = [{ dx: 0, dy: 0 }]
  let x = 0, y = 0, step = 1
  while (offsets.length < capacity) {
    for (let i = 0; i < step && offsets.length < capacity; i++) { x++; offsets.push({ dx: x, dy: y }) }
    for (let i = 0; i < step && offsets.length < capacity; i++) { y++; offsets.push({ dx: x, dy: y }) }
    step++
    for (let i = 0; i < step && offsets.length < capacity; i++) { x--; offsets.push({ dx: x, dy: y }) }
    for (let i = 0; i < step && offsets.length < capacity; i++) { y--; offsets.push({ dx: x, dy: y }) }
    step++
  }
  return offsets
}

function innerGridSize(count: number): number {
  const side = Math.ceil(Math.sqrt(count))
  return side % 2 === 0 ? side + 1 : side
}

// --- Plotted risk type for export ---

interface ExportRisk {
  id: string
  name: string
  likelihood: number
  impact: number
  outsideAppetite: boolean
  ownerDisplayName: string
  number?: number // table row number (if in top-N)
}

// --- SVG building ---

function escapeXml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

function truncate(s: string, max: number): string {
  return s.length > max ? s.slice(0, max - 1) + '…' : s
}

function renderGrid(
  config: GridConfig,
  appetite: AppetiteMap,
  risks: ExportRisk[],
  c: ReturnType<typeof getThemeColours>,
  dotRadius: number,
): string {
  const cols = config.likelihoodLevels.length
  const rows = config.impactLevels.length
  const cw = cellWidth(cols)
  const ch = cellHeight(rows)
  const hasAppetite = Object.keys(appetite).length > 0
  const lines: string[] = []

  // Cells
  for (const lLevel of config.likelihoodLevels) {
    for (const iLevel of config.impactLevels) {
      const o = cellOrigin(lLevel.value, iLevel.value, cols, rows)
      const key = appetiteKey(lLevel.value, iLevel.value)
      const inApp = hasAppetite ? appetite[key] === true : true
      lines.push(
        `<rect x="${o.x}" y="${o.y}" width="${cw}" height="${ch}" fill="${inApp ? c.appetiteIn : c.appetiteOut}" stroke="${c.border}" stroke-width="1"/>`,
      )
    }
  }

  // Y-axis labels
  for (const level of config.impactLevels) {
    const o = cellOrigin(1, level.value, cols, rows)
    lines.push(
      `<text x="${MARGIN.left - 6}" y="${o.y + ch / 2}" dy="0.35em" text-anchor="end" font-size="11" fill="${c.mutedFg}">${level.value}</text>`,
    )
  }

  // Y-axis title
  const yMid = MARGIN.top + (VIEWBOX_H - MARGIN.top - MARGIN.bottom) / 2
  lines.push(
    `<text x="12" y="${yMid}" dy="0.35em" text-anchor="middle" font-size="11" fill="${c.mutedFg}" transform="rotate(-90, 12, ${yMid})">Impact →</text>`,
  )

  // X-axis labels
  for (const level of config.likelihoodLevels) {
    const o = cellOrigin(level.value, 1, cols, rows)
    lines.push(
      `<text x="${o.x + cw / 2}" y="${MARGIN.top + rows * ch + 18}" text-anchor="middle" font-size="11" fill="${c.mutedFg}">${level.value}</text>`,
    )
  }

  // X-axis title
  const xMid = MARGIN.left + (VIEWBOX_W - MARGIN.left - MARGIN.right) / 2
  lines.push(
    `<text x="${xMid}" y="${VIEWBOX_H - 8}" text-anchor="middle" font-size="11" fill="${c.mutedFg}">Likelihood →</text>`,
  )

  // Dots
  const cellGroups = new Map<string, ExportRisk[]>()
  for (const risk of risks) {
    const key = `${risk.likelihood}-${risk.impact}`
    const group = cellGroups.get(key) ?? []
    group.push(risk)
    cellGroups.set(key, group)
  }

  const inset = 4
  for (const [, group] of cellGroups) {
    const first = group[0]!
    const origin = cellOrigin(first.likelihood, first.impact, cols, rows)
    const count = group.length
    const gridN = innerGridSize(count)
    const offsets = spiralOffsets(count)
    const areaW = cw - inset * 2
    const areaH = ch - inset * 2
    const subW = areaW / gridN
    const subH = areaH / gridN
    const centerX = origin.x + cw / 2
    const centerY = origin.y + ch / 2

    for (let i = 0; i < count; i++) {
      const risk = group[i]!
      const offset = offsets[i]!
      const cx = centerX + offset.dx * subW
      const cy = centerY + offset.dy * subH
      const fill = risk.outsideAppetite ? c.dotInherent : c.dotResidual

      lines.push(`<circle cx="${cx}" cy="${cy}" r="${dotRadius}" fill="${fill}" stroke="#ffffff" stroke-width="1"/>`)
      if (risk.number !== undefined) {
        lines.push(
          `<text x="${cx}" y="${cy}" dy="0.35em" text-anchor="middle" font-size="8" font-weight="bold" fill="#ffffff">${risk.number}</text>`,
        )
      }
    }
  }

  return lines.join('\n')
}

function renderTable(
  risks: ExportRisk[],
  tableSize: number,
  totalCount: number,
  c: ReturnType<typeof getThemeColours>,
  offsetX: number,
): string {
  const sorted = [...risks].sort((a, b) => {
    const sa = a.likelihood * a.impact
    const sb = b.likelihood * b.impact
    if (sb !== sa) return sb - sa
    return b.likelihood - a.likelihood
  })
  const displayed = sorted.slice(0, tableSize)
  const lines: string[] = []

  const colX = {
    num: offsetX + 10,
    name: offsetX + 35,
    owner: offsetX + 260,
    l: offsetX + 380,
    i: offsetX + 410,
    score: offsetX + 440,
    warn: offsetX + 478,
  }
  const tableW = 490
  const rowH = 20
  const headerY = 0
  const bodyStartY = headerY + rowH + 4

  // Header background
  lines.push(
    `<rect x="${offsetX}" y="${headerY - 2}" width="${tableW}" height="${rowH + 2}" rx="3" fill="${c.border}" opacity="0.4"/>`,
  )

  // Header text
  const hY = headerY + rowH / 2
  lines.push(`<text x="${colX.num}" y="${hY}" dy="0.35em" font-size="10" font-weight="600" fill="${c.mutedFg}">#</text>`)
  lines.push(`<text x="${colX.name}" y="${hY}" dy="0.35em" font-size="10" font-weight="600" fill="${c.mutedFg}">Risk</text>`)
  lines.push(`<text x="${colX.owner}" y="${hY}" dy="0.35em" font-size="10" font-weight="600" fill="${c.mutedFg}">Owner</text>`)
  lines.push(`<text x="${colX.l}" y="${hY}" dy="0.35em" text-anchor="middle" font-size="10" font-weight="600" fill="${c.mutedFg}">L</text>`)
  lines.push(`<text x="${colX.i}" y="${hY}" dy="0.35em" text-anchor="middle" font-size="10" font-weight="600" fill="${c.mutedFg}">I</text>`)
  lines.push(`<text x="${colX.score}" y="${hY}" dy="0.35em" text-anchor="middle" font-size="10" font-weight="600" fill="${c.mutedFg}">Score</text>`)

  // Rows
  for (let i = 0; i < displayed.length; i++) {
    const risk = displayed[i]!
    const rY = bodyStartY + i * rowH
    const tY = rY + rowH / 2
    const score = risk.likelihood * risk.impact

    // Row separator
    if (i > 0) {
      lines.push(`<line x1="${offsetX}" y1="${rY}" x2="${offsetX + tableW}" y2="${rY}" stroke="${c.border}" stroke-width="0.5"/>`)
    }

    lines.push(`<text x="${colX.num}" y="${tY}" dy="0.35em" font-size="9" font-weight="bold" fill="${c.mutedFg}">${i + 1}</text>`)
    lines.push(`<text x="${colX.name}" y="${tY}" dy="0.35em" font-size="10" fill="${c.foreground}">${escapeXml(truncate(risk.name, 30))}</text>`)
    lines.push(`<text x="${colX.owner}" y="${tY}" dy="0.35em" font-size="9" fill="${c.mutedFg}">${escapeXml(truncate(risk.ownerDisplayName || '—', 16))}</text>`)
    lines.push(`<text x="${colX.l}" y="${tY}" dy="0.35em" text-anchor="middle" font-size="10" fill="${c.foreground}">${risk.likelihood}</text>`)
    lines.push(`<text x="${colX.i}" y="${tY}" dy="0.35em" text-anchor="middle" font-size="10" fill="${c.foreground}">${risk.impact}</text>`)
    lines.push(`<text x="${colX.score}" y="${tY}" dy="0.35em" text-anchor="middle" font-size="10" font-weight="600" fill="${c.foreground}">${score}</text>`)

    if (risk.outsideAppetite) {
      // Small warning triangle
      const wx = colX.warn
      const wy = tY
      lines.push(`<text x="${wx}" y="${wy}" dy="0.35em" text-anchor="middle" font-size="11" fill="${c.destructive}">⚠</text>`)
    }
  }

  // "Showing X of Y" footer
  if (totalCount > displayed.length) {
    const footY = bodyStartY + displayed.length * rowH + 16
    lines.push(
      `<text x="${offsetX + tableW / 2}" y="${footY}" text-anchor="middle" font-size="9" fill="${c.mutedFg}">Showing ${displayed.length} of ${totalCount} risks (sorted by score)</text>`,
    )
  }

  return lines.join('\n')
}

// --- Public export functions ---

export interface HeatmapExportParams {
  title: string
  breadcrumb: string
  config: GridConfig
  appetite: AppetiteMap
  risks: HeatmapRisk[]
  tableSize: number
  totalRiskCount: number
  outOfAppetiteCount: number
  /** Optional filter label shown below the breadcrumb (e.g. "Filtered by: Cyber Risk"). */
  filterLabel?: string
}

function buildExportRisks(risks: HeatmapRisk[], tableSize: number): ExportRisk[] {
  // Sort by score descending for numbering
  const sorted = [...risks].sort((a, b) => {
    const sa = a.residual_likelihood * a.residual_max_impact
    const sb = b.residual_likelihood * b.residual_max_impact
    if (sb !== sa) return sb - sa
    return b.residual_likelihood - a.residual_likelihood
  })

  return risks.map((r) => {
    const sortedIdx = sorted.findIndex((s) => s.id === r.id)
    return {
      id: r.id,
      name: r.name,
      likelihood: r.residual_likelihood,
      impact: r.residual_max_impact,
      outsideAppetite: r.outside_appetite,
      ownerDisplayName: r.owner_display_name,
      number: sortedIdx < tableSize ? sortedIdx + 1 : undefined,
    }
  })
}

/** Export grid only (no table). */
export function exportHeatmapGridSvg(params: HeatmapExportParams): string {
  const c = getThemeColours()
  const exportRisks = buildExportRisks(params.risks, params.tableSize)

  const headerH = headerHeight(params)
  const footerH = params.filterLabel ? 34 : 20
  const totalH = headerH + VIEWBOX_H + footerH
  const totalW = VIEWBOX_W

  const header = buildHeader(params, c, 0, totalW)
  const gridContent = renderGrid(params.config, params.appetite, exportRisks, c, 8)
  const footer = buildFooter(params, c, totalW, totalH)

  return [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${totalW} ${totalH}" width="${totalW}" height="${totalH}" font-family="system-ui, -apple-system, sans-serif">`,
    `<rect width="${totalW}" height="${totalH}" fill="white"/>`,
    `<g transform="translate(0, ${headerH})">`,
    gridContent,
    '</g>',
    header,
    footer,
    '</svg>',
  ].join('\n')
}

/** Export grid + table side by side. */
export function exportHeatmapFullSvg(params: HeatmapExportParams): string {
  const c = getThemeColours()
  const exportRisks = buildExportRisks(params.risks, params.tableSize)

  const headerH = headerHeight(params)
  const tableOffsetX = VIEWBOX_W + 20
  const tableW = 490
  const totalW = tableOffsetX + tableW + 10
  const footerH = params.filterLabel ? 34 : 20
  const totalH = headerH + VIEWBOX_H + footerH

  const header = buildHeader(params, c, 0, totalW)
  const gridContent = renderGrid(params.config, params.appetite, exportRisks, c, 8)
  const tableContent = renderTable(exportRisks, params.tableSize, params.totalRiskCount, c, tableOffsetX)
  const footer = buildFooter(params, c, totalW, totalH)

  return [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${totalW} ${totalH}" width="${totalW}" height="${totalH}" font-family="system-ui, -apple-system, sans-serif">`,
    `<rect width="${totalW}" height="${totalH}" fill="white"/>`,
    `<g transform="translate(0, ${headerH})">`,
    gridContent,
    '</g>',
    // Table aligned to the top of the grid area
    `<g transform="translate(0, ${headerH + MARGIN.top})">`,
    tableContent,
    '</g>',
    header,
    footer,
    '</svg>',
  ].join('\n')
}

/** Height of the header area (no header content, just a small top margin). */
function headerHeight(_params: HeatmapExportParams): number {
  return 0
}

/** Render footer at bottom-right: filter label (if any) + generated date. */
function buildFooter(
  params: HeatmapExportParams,
  c: ReturnType<typeof getThemeColours>,
  totalW: number,
  totalH: number,
): string {
  const lines: string[] = []
  const x = totalW - 10
  const now = new Date()
  const dateStr = now.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  const stamp = `Generated ${dateStr}`

  let y = totalH - 6
  lines.push(
    `<text x="${x}" y="${y}" text-anchor="end" font-size="9" fill="${c.mutedFg}">${escapeXml(stamp)}</text>`,
  )
  if (params.filterLabel) {
    y -= 14
    lines.push(
      `<text x="${x}" y="${y}" text-anchor="end" font-size="10" font-style="italic" fill="${c.mutedFg}">${escapeXml(params.filterLabel)}</text>`,
    )
  }
  return lines.join('\n')
}

/** Header is no longer rendered — context is provided by the collapsible section
 *  header in the UI and the footer in the exported SVG. Kept as a stub so the
 *  call sites don't need restructuring. */
function buildHeader(
  _params: HeatmapExportParams,
  _c: ReturnType<typeof getThemeColours>,
  _x: number,
  _totalW: number,
): string {
  return ''
}

/** Trigger a file download in the browser. */
export function downloadSvg(svgContent: string, filename: string): void {
  const blob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
